import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import * as PortOne from "@portone/browser-sdk/v2";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";

// 고유 ID 생성 함수
const encodeUUID = () => {
  const randomPart = Math.random().toString(36).substring(2, 10);
  const timestampPart = Date.now().toString(36);
  return `${randomPart}${timestampPart}`;
};

const PaymentPage = () => {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const [isChecked, setIsChecked] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [totalAmount, setTotalAmount] = useState(10000);

  // 결제 금액 계산 (본인 포함 인원수 × 10000원)
  useEffect(() => {
    const applicationType = Cookies.get("applicationType");

    // 대표신청자인 경우 공동신청자 수를 고려하여 계산
    if (applicationType === "representative") {
      const totalApplicants = parseInt(
        Cookies.get("totalApplicants") || "1",
        10
      );
      const calculatedAmount = totalApplicants * 10000;
      setTotalAmount(calculatedAmount);
      Cookies.set("lawsuit_amount", calculatedAmount, {
        expires: 7,
        path: "/",
      });
    } else {
      // 본인 신청인 경우 1명만
      setTotalAmount(10000);
      Cookies.set("lawsuit_amount", 10000, {
        expires: 7,
        path: "/",
      });
    }
  }, []);

  // 사용자 정보 가져오기
  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        const token = Cookies.get(
          API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN
        );

        if (!token) {
          throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
        }

        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/clients/me`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error("사용자 정보를 가져오는데 실패했습니다.");
        }

        const data = await response.json();
        setUserData(data);
      } catch (err) {
        setError(err.message || "사용자 정보를 가져오는데 실패했습니다.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, []);

  // 결제 완료 API 호출
  const completePayment = async (paymentId) => {
    try {
      const token = Cookies.get(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);
      if (!token) {
        throw new Error("인증 토큰이 없습니다.");
      }

      const applicationNumber = Cookies.get("application_number");
      if (!applicationNumber) {
        throw new Error("신청번호가 없습니다.");
      }

      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/payments/complete`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            paymentId: paymentId,
            applicationNumber: applicationNumber,
            price: totalAmount,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "결제 완료 처리에 실패했습니다.");
      }

      return await response.json();
    } catch (error) {
      // 결제 취소 시도
      const cancelSuccess = await cancelPayment(paymentId);

      if (cancelSuccess) {
      } else {
        Object.keys(Cookies.get()).forEach((cookie) => {
          Cookies.remove(cookie);
        });
        // 결제는 성공했으나 취소 실패했으므로 완료 페이지로 이동
        navigate("/complete");
      }
      throw error;
    }
  };

  // 결제 취소 API 호출
  const cancelPayment = async (
    paymentId,
    reason = "결제 완료 처리 실패로 인한 취소"
  ) => {
    try {
      const token = Cookies.get(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);
      if (!token) {
        throw new Error("인증 토큰이 없습니다.");
      }

      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/payments/cancel`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            paymentId: paymentId,
            reason: reason,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        return false;
      }

      return true;
    } catch (error) {
      return false;
    }
  };

  // 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  const processPayment = async () => {
    if (!isChecked) {
      return;
    }

    if (!userData) {
      return;
    }

    try {
      setIsPaymentProcessing(true);
      const applicationNumber = Cookies.get("application_number");

      // 환경 변수 값을 직접 사용
      const PORTONE_STORE_ID = "store-c0cd2b2d-8f6e-4586-89d3-b545b2a73a8f";
      const KAKAOPAY_CHANNEL_KEY =
        "channel-key-5e5e130e-f83f-4da7-b115-45204e6be7a4";

      const paymentId = `pay_${encodeUUID()}`;
      const response = await PortOne.requestPayment({
        // Store ID 설정
        storeId: PORTONE_STORE_ID, // process.env.REACT_APP_PORTONE_STORE_ID 대신 직접 값 사용
        // 채널 키 설정
        channelKey: KAKAOPAY_CHANNEL_KEY, // process.env.REACT_APP_KAKAOPAY_CHANNEL_KEY 대신 직접 값 사용
        paymentId,
        orderName: "쿠팡 집단소송 접수비",
        totalAmount: totalAmount,
        currency: "CURRENCY_KRW",
        payMethod: "EASY_PAY",
        bypass: {
          kakaopay: {
            custom_message: "하기 금액은 부가세 10%가 포함된 금액입니다.",
          },
        },
        customData: {
          applicationNumber: applicationNumber,
          price: totalAmount,
        },
        noticeUrls: ["https://lawsuit.aitheh.com/payments/complete"],
        customer: {
          fullName: userData.name || "사용자",
          phoneNumber: userData.phoneNumber || "01012345678",
          email: userData.email || "user@example.com",
        },
        redirectUrl: `https://www.lawfirmthe-h-coupang.info/payment-verification?paymentId=${paymentId}`,
        // redirectUrl: `http://172.30.1.33:3000/payment-verification?paymentId=${paymentId}`,
      });

      if (response.code != null) {
        setIsPaymentProcessing(false);
        return;
      }

      if (response.paymentId) {
        try {
          navigate(`/payment-verification?paymentId=${paymentId}`);

          // 결제 완료 API 호출
          // await completePayment(response.paymentId);//
          // 결제 성공 - 쿠키는 유지하고 결제 확인 페이지에서 처리되도록 함
          // 포트원에서 자동으로 redirectUrl로 리다이렉트됨
        } catch (completeError) {}
      }
    } catch (error) {
    } finally {
      setIsPaymentProcessing(false);
    }
  };

  // 로딩 표시
  if (isLoading) {
    return (
      <PageBackground>
        <Container>
          <Content>
            <LoadingMessage>사용자 정보를 불러오는 중입니다...</LoadingMessage>
          </Content>
        </Container>
      </PageBackground>
    );
  }

  // 에러 표시
  if (error) {
    return (
      <PageBackground>
        <Container>
          <Content>
            <ErrorMessage>{error}</ErrorMessage>
            <RetryButton onClick={() => window.location.reload()}>
              다시 시도하기
            </RetryButton>
          </Content>
        </Container>
      </PageBackground>
    );
  }

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          <ContentSection>
            <PageHeader>결제하기</PageHeader>
            <TitleWithIcon>
              <PayIcon src="/png/payIcon.png" alt="결제 아이콘" />
              <SectionTitle>결제하기</SectionTitle>
            </TitleWithIcon>
            <SectionSubtitle>
              소송 접수를 위한 비용을 결제해주세요
            </SectionSubtitle>

            {userData && (
              <UserInfoBox>
                <BoxTitle>결제자 정보</BoxTitle>
                <DetailRow>
                  <DetailLabel>이름</DetailLabel>
                  <DetailValue>{userData.name}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>연락처</DetailLabel>
                  <DetailValue>{userData.phoneNumber}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>이메일</DetailLabel>
                  <DetailValue>{userData.email}</DetailValue>
                </DetailRow>
              </UserInfoBox>
            )}

            <PaymentDetailsBox>
              <BoxTitle>결제 금액 상세</BoxTitle>
              <Divider />
              <TotalRow>
                <TotalLabel>총 결제 금액</TotalLabel>
                <TotalValue>{totalAmount.toLocaleString()}원</TotalValue>
              </TotalRow>
            </PaymentDetailsBox>

            <PaymentMethodSection>
              <SectionLabel>결제수단</SectionLabel>
              <KakaoPayContainer>
                <KakaoPayRow>
                  <KakaoPayIcon
                    src="/png/kakaopayIcon.png"
                    alt="카카오페이 아이콘"
                  />
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "2px",
                    }}
                  >
                    <KakaoPayTitle>카카오페이</KakaoPayTitle>
                    <KakaoPayDescription>
                      간편하고 안전한 모바일 결제
                    </KakaoPayDescription>
                  </div>
                </KakaoPayRow>
              </KakaoPayContainer>
            </PaymentMethodSection>

            <TermsSection>
              <TermsTitle>환불 규정 및 주의사항</TermsTitle>
              <TermsList>
                <TermsItem>
                  • 착수금 또는 관리비는 소송 진행을 위해 필요한 비용입니다.
                </TermsItem>
                <TermsItem>
                  • 납부 후 취소 시 접수비는 환불되지 않습니다.
                </TermsItem>
                <TermsItem>
                  • 소송 접수 후 취소 시 송달료는 실비 정산 후 환불됩니다.
                </TermsItem>
                <TermsItem>
                  • 소송 진행 중 항소 단계에서만 추가 비용이 발생할 수 있으며,
                  패소에 따른 별도 비용은 없습니다. 추가 비용이 생길 경우 사전에
                  안내해 드립니다.
                </TermsItem>
              </TermsList>

              <CheckboxContainer onClick={() => setIsChecked(!isChecked)}>
                <Checkbox checked={isChecked}>
                  {isChecked ? (
                    <CheckedImg src="/png/checkedYellow.png" alt="체크박스" />
                  ) : (
                    <CheckedImg src="/png/checkedinActive.png" alt="체크박스" />
                  )}
                </Checkbox>
                <CheckboxLabel>
                  상기 내용을 모두 확인하였으며 동의합니다.
                </CheckboxLabel>
              </CheckboxContainer>
            </TermsSection>

            <ButtonContainer>
              <PaymentButton
                onClick={processPayment}
                disabled={!isChecked || isPaymentProcessing}
              >
                {isPaymentProcessing
                  ? "결제 처리 중..."
                  : "카카오페이로 결제하기"}
              </PaymentButton>
            </ButtonContainer>
            <div style={{ height: "100px" }} />
          </ContentSection>
        </Content>
      </Container>
    </PageBackground>
  );
};

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: #f8f8f8;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;

  @media (min-width: 769px) {
    padding: 20px;
    background-color: #fff;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;
  background-color: #f8f8f8;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: 100vh;
    box-shadow: none;
    padding: 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 768px) {
    background-color: #f8f8f8;
    border-radius: 20px;
    min-height: 700px;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  position: relative;

  @media (max-width: 768px) {
    padding: 0;
    min-height: 100vh;
  }

  @media (min-width: 769px) {
    min-height: 700px;
    padding: 0;
  }
`;

const PageHeader = styled.h1`
  font-size: 17px;
  font-weight: 600;
  text-align: center;
  padding: 20px 0;
  margin: 0;
  color: #00285b;
`;

const ContentSection = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const TitleWithIcon = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 10px;
  flex-direction: column;
`;

const PayIcon = styled.img`
  width: 50px;
  height: 29px;
  margin-right: 10px;
  margin-bottom: 10px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 10px 0;
  margin-bottom: -5px;
  color: #333;
`;

const SectionSubtitle = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0 0 20px 0;
`;

const PaymentDetailsBox = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
`;

const BoxTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 20px 0;
  color: #333;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
`;

const DetailLabel = styled.span`
  color: #666;
`;

const DetailValue = styled.span`
  font-weight: 500;
  color: #333;
`;

const Divider = styled.div`
  height: 1px;
  background-color: #eee;
  margin: 15px 0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TotalLabel = styled.span`
  font-weight: 600;
  color: #333;
`;

const TotalValue = styled.span`
  font-weight: 700;
  font-size: 18px;
  color: #333;
`;

const PaymentMethodSection = styled.div`
  margin-bottom: 24px;
`;

const SectionLabel = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: #333;
`;

const KakaoPayContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 12px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  border-radius: 12px;
  border: 1px solid #ffeb00;
  background: #fefce6;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
`;

const KakaoPayRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const KakaoPayIcon = styled.img`
  width: 40px;
  height: 40px;
`;

const KakaoPayTitle = styled.span`
  color: #131313;
  font-family: Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`;

const KakaoPayDescription = styled.span`
  color: #4b5563;
  font-family: Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`;

const TermsSection = styled.div`
  margin-bottom: 20px;
`;

const TermsTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: #2e3840;
`;

const TermsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-bottom: 20px;
`;

const TermsItem = styled.p`
  font-size: 12px;
  color: #4b5563;
  margin: 0;
  line-height: 1.5;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const Checkbox = styled.div`
  width: 20px;
  height: 20px;
  ${
    "" /* border: 1px solid ${(props) => (props.checked ? "#ffe600" : "#ddd")}; */
  }
  background-color: ${(props) => (props.checked ? "#ffe600" : "white")};
  border-radius: 4px;
  margin-right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CheckIcon = styled.span`
  color: white;
  font-size: 14px;
  font-weight: bold;
`;

const CheckboxLabel = styled.span`
  font-size: 14px;
  color: #131313;
  font-weight: 500;
`;

const CheckedImg = styled.img`
  width: 100%;
  height: 100%;
`;

const ButtonContainer = styled.div`
  width: 100%;
  background-color: #f8f8f8;
`;

const PaymentButton = styled.button`
  width: 100%;
  height: 50px;
  background-color: ${(props) => (props.disabled ? "#aaa" : "#ffe600")};
  color: ${(props) => (props.disabled ? "#fff" : "#000")};
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
`;

// 추가 스타일
const LoadingMessage = styled.div`
  padding: 20px;
  text-align: center;
  font-size: 16px;
  color: #666;
`;

const ErrorMessage = styled.div`
  padding: 20px;
  text-align: center;
  font-size: 16px;
  color: #ff4c4c;
  margin-bottom: 20px;
`;

const RetryButton = styled.button`
  background-color: #0075ff;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 16px;
  cursor: pointer;
  display: block;
  margin: 0 auto;
`;

const UserInfoBox = styled(PaymentDetailsBox)`
  margin-bottom: 16px;
`;

export default PaymentPage;
